import { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, IconButton, MenuItem, Paper, Stack, TextField, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CaseStatus } from '@bandido/shared/constants';
import { DASHBOARD_MOBILE_NAV_HEIGHT } from '@frontend/features/common/constants/layout';
import { useMobileViewport } from '@frontend/features/common/hooks/useMobileViewport';
import { useCaseBulkActions } from '../hooks/useCaseBulkActions';

export const CaseBulkActionsMobile = ({ bulk }: { bulk: ReturnType<typeof useCaseBulkActions> }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const viewport = useMobileViewport(open);
  const count = bulk.selectedIds.length;
  useEffect(() => {
    if (!count && !bulk.isBulkUpdating) setOpen(false);
  }, [count, bulk.isBulkUpdating]);
  const close = () => {
    if (!bulk.isBulkUpdating) setOpen(false);
  };
  const fields = [
    { key: 'workflow', value: bulk.bulkWorkflowId, set: bulk.setBulkWorkflowId, options: bulk.workflowOptions },
    {
      key: 'step',
      value: bulk.bulkStepId,
      set: bulk.setBulkStepId,
      options: bulk.stepOptions,
      disabled: !bulk.bulkWorkflowId || bulk.isLoadingSteps,
    },
    {
      key: 'status',
      value: bulk.bulkStatus,
      set: (value: string) => bulk.setBulkStatus(value as CaseStatus | ''),
      options: bulk.statusOptions,
    },
    {
      key: 'agent',
      value: bulk.bulkAgentValue,
      set: bulk.setBulkAgentValue,
      options: [
        { id: 'unassigned', name: t('cases.list.bulk.unassigned') },
        ...bulk.agentOptions.map((agent) => ({
          id: agent.clientId,
          name: [agent.firstName, agent.lastName].filter(Boolean).join(' '),
        })),
      ],
    },
  ];
  return (
    <>
      {count > 0 && (
        <Paper
          component="section"
          aria-label={t('cases.list.bulk.title')}
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: DASHBOARD_MOBILE_NAV_HEIGHT,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            p: 1.5,
            minHeight: 72,
          }}
        >
          <Stack direction="row" alignItems="center" gap={1}>
            <IconButton
              aria-label={t('cases.list.bulk.clear')}
              disabled={bulk.isBulkUpdating}
              onClick={bulk.clearSelection}
            >
              <Close />
            </IconButton>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }} aria-live="polite">
              {t('cases.list.bulk.selectedCount', { count })}
            </Typography>
            <Button
              variant="contained"
              disabled={bulk.isUnavailable || bulk.isBulkUpdating}
              onClick={() => setOpen(true)}
            >
              {t('cases.list.bulk.actions')}
            </Button>
          </Stack>
        </Paper>
      )}
      <Dialog
        open={open}
        fullScreen
        onClose={close}
        aria-labelledby="case-bulk-title"
        PaperProps={{
          sx: {
            position: 'fixed',
            ...viewport,
            maxHeight: 'none',
            pt: 'env(safe-area-inset-top)',
            pb: 'env(safe-area-inset-bottom)',
            '& input, & .MuiSelect-select': { fontSize: 16 },
            '& button': { minHeight: 44, minWidth: 44 },
          },
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} p={2} flexShrink={0}>
          <Typography id="case-bulk-title" component="h2" variant="h6" flex={1}>
            {t('cases.list.bulk.title')}
          </Typography>
          <IconButton aria-label={t('common.close')} disabled={bulk.isBulkUpdating} onClick={close}>
            <Close />
          </IconButton>
        </Stack>
        <Stack gap={2.5} px={2} py={1} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Typography variant="body2">{t('cases.list.bulk.scope', { count })}</Typography>
          {fields.map((field) => (
            <TextField
              key={field.key}
              id={`case-bulk-${field.key}`}
              select
              fullWidth
              size="small"
              label={t(`cases.list.bulk.${field.key}`)}
              value={field.value}
              disabled={bulk.isBulkUpdating || field.disabled}
              onChange={(event) => field.set(event.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: (value) =>
                  field.options.find((option) => option.id === value)?.name || t('cases.list.bulk.leaveUnchanged'),
                MenuProps: {
                  PaperProps: {
                    sx: {
                      maxHeight: '50dvh',
                      '& .MuiMenuItem-root': { minHeight: 44, whiteSpace: 'normal', overflowWrap: 'anywhere' },
                    },
                  },
                },
              }}
            >
              <MenuItem value="">{t('cases.list.bulk.leaveUnchanged')}</MenuItem>
              {field.options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
          ))}
          {bulk.hasChanges && (
            <Box sx={{ bgcolor: 'action.hover', p: 1.5, borderRadius: 1, overflowWrap: 'anywhere' }}>
              <Typography fontWeight={600} variant="body2" mb={1}>
                {t('cases.list.bulk.summary')}
              </Typography>
              {fields
                .filter((field) => field.value)
                .map((field) => (
                  <Typography key={field.key} variant="body2">
                    {t(`cases.list.bulk.${field.key}`)}:{' '}
                    {field.options.find((option) => option.id === field.value)?.name || field.value}
                  </Typography>
                ))}
            </Box>
          )}
          {bulk.error && <Alert severity="error">{t('cases.list.bulk.partialError')}</Alert>}
        </Stack>
        <Stack direction="row" gap={1} p={2} sx={{ flexShrink: 0, borderTop: 1, borderColor: 'divider' }}>
          <Button disabled={bulk.isBulkUpdating} onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            sx={{ flex: 1 }}
            disabled={!count || !bulk.hasChanges || bulk.isUnavailable || bulk.isBulkUpdating}
            onClick={async () => {
              if (await bulk.handleApplyBulkUpdate()) setOpen(false);
            }}
          >
            {t('cases.list.bulk.applyCount', { count })}
          </Button>
        </Stack>
      </Dialog>
    </>
  );
};
